import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import ReservationDesk from './ReservationDesk';
import {
  LayoutGrid, CalendarDays, Settings2, Upload, Sparkles, Plus, Trash2, Save, Check, X, Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TM = `${API}/api/table-management`;
const TABS = [
  { id: 'floor', label: 'Tables & Floor Plan', icon: LayoutGrid },
  { id: 'reservations', label: 'Reservations', icon: CalendarDays },
  { id: 'settings', label: 'Booking Settings', icon: Settings2 },
];

function useApi(token) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  return useCallback(async (url, options = {}) => {
    const isForm = options.body instanceof FormData;
    const res = await fetch(url, { ...options, headers: { ...headers, ...(isForm ? {} : { 'Content-Type': 'application/json' }) } });
    const body = await res.json().catch(() => null);
    if (!res.ok || !body) throw new Error(typeof body?.detail === 'string' ? body.detail : 'Request failed');
    return body;
  }, [headers]);
}

/** Back office: tables, floor plan (upload + AI layout), reservations and booking settings. */
export default function TablesReservationsPage() {
  const { token } = useOutletContext();
  const api = useApi(token);
  const [tab, setTab] = useState('floor');
  return (
    <div className="max-w-6xl mx-auto" data-testid="tables-reservations-page">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Tables & Reservations</h1>
      <p className="text-sm text-slate-500 mb-4">The same tables your POS uses and guests book online.</p>
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700 mb-5 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 ${tab === t.id ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === 'floor' && <FloorPlanTab api={api} />}
      {tab === 'reservations' && <ReservationDesk api={api} TM={TM} />}
      {tab === 'settings' && <SettingsTab api={api} />}
    </div>
  );
}

/* ===================== Floor plan ===================== */

function FloorPlanTab({ api }) {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(null);
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dirty, setDirty] = useState({});
  const [proposal, setProposal] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const plan = plans.find(p => p.id === planId);

  const load = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([api(`${TM}/floor-plans`), api(`${TM}/tables`)]);
      setPlans(p.floor_plans);
      setTables(t.tables);
      setPlanId(prev => prev || p.floor_plans[0]?.id || null);
    } catch (e) { toast.error(e.message); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const createPlan = async () => {
    const name = window.prompt('Name this area (e.g. Main Dining, Patio)');
    if (!name) return;
    try {
      const r = await api(`${TM}/floor-plans`, { method: 'POST', body: JSON.stringify({ name, width: 1000, height: 600 }) });
      setPlanId(r.floor_plan_id);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const uploadImage = async (file) => {
    if (!file || !plan) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const up = await api(`${API}/api/uploads/image?folder=floor-plans`, { method: 'POST', body: form });
      await api(`${TM}/floor-plans/${plan.id}`, { method: 'PATCH', body: JSON.stringify({ background_image: up.url }) });
      toast.success('Floor plan image added');
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const planTables = tables.filter(t => t.floor_plan_id === planId || (!t.floor_plan_id && plans[0]?.id === planId));

  // Dragging: positions are kept in plan pixels; the canvas scales to its width.
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const scale = () => (canvasRef.current && plan ? canvasRef.current.clientWidth / plan.width : 1);
  const onPointerDown = (e, t) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setSelected(t.id);
    dragRef.current = { id: t.id, startX: e.clientX, startY: e.clientY, x: t.pos_x, y: t.pos_y };
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || !plan) return;
    const k = scale();
    const x = Math.max(0, Math.min(plan.width - 20, Math.round(d.x + (e.clientX - d.startX) / k)));
    const y = Math.max(0, Math.min(plan.height - 20, Math.round(d.y + (e.clientY - d.startY) / k)));
    setTables(prev => prev.map(t => (t.id === d.id ? { ...t, pos_x: x, pos_y: y } : t)));
    setDirty(prev => ({ ...prev, [d.id]: true }));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const savePositions = async () => {
    try {
      const positions = tables.filter(t => dirty[t.id]).map(t => ({ table_id: t.id, pos_x: t.pos_x, pos_y: t.pos_y, rotation: t.rotation }));
      await api(`${TM}/tables/bulk-update-positions`, { method: 'POST', body: JSON.stringify(positions) });
      setDirty({});
      toast.success('Layout saved');
    } catch (e) { toast.error(e.message); }
  };

  const applyProposal = async (replace) => {
    try {
      const r = await api(`${TM}/floor-plans/${planId}/apply-layout`, { method: 'POST', body: JSON.stringify({ tables: proposal, replace_existing: replace }) });
      toast.success(`Layout applied: ${r.created} new, ${r.moved} moved${r.retired ? `, ${r.retired} removed` : ''}`);
      setProposal(null);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const shown = proposal || planTables;
  const sel = tables.find(t => t.id === selected);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <select className="h-9 rounded-md border border-slate-200 bg-white dark:bg-slate-800 px-2 text-sm" value={planId || ''}
          onChange={(e) => setPlanId(Number(e.target.value))} data-testid="plan-select">
          {plans.length === 0 && <option value="">No floor plan yet</option>}
          {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <Button variant="outline" size="sm" onClick={createPlan}><Plus className="w-4 h-4 mr-1" /> Area</Button>
        {plan && <>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="upload-plan-btn">
            <Upload className="w-4 h-4 mr-1" /> {plan.background_image ? 'Replace' : 'Upload'} floor plan image
          </Button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
          <Button size="sm" onClick={() => setAiOpen(true)} data-testid="ai-layout-btn"><Sparkles className="w-4 h-4 mr-1" /> AI layout</Button>
          <AddTableButton api={api} planId={planId} onAdded={load} />
          {Object.keys(dirty).length > 0 && <Button size="sm" variant="outline" onClick={savePositions}><Save className="w-4 h-4 mr-1" /> Save layout</Button>}
        </>}
      </div>

      {proposal && (
        <div className="rounded-lg border border-violet-300 bg-violet-50 dark:bg-slate-800 p-3 flex flex-wrap items-center gap-2" data-testid="ai-proposal">
          <Sparkles className="w-4 h-4 text-violet-600" />
          <span className="text-sm">AI proposed {proposal.length} tables ({proposal.reduce((s, t) => s + t.capacity, 0)} seats). Review, then apply.</span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setProposal(null)}><X className="w-4 h-4 mr-1" /> Discard</Button>
            <Button size="sm" variant="outline" onClick={() => applyProposal(false)}>Apply (keep other tables)</Button>
            <Button size="sm" onClick={() => applyProposal(true)}><Check className="w-4 h-4 mr-1" /> Apply & replace</Button>
          </div>
        </div>
      )}

      {plan ? (
        <div className="grid lg:grid-cols-[1fr_260px] gap-4">
          <div ref={canvasRef} className="relative w-full rounded-lg border border-slate-200 overflow-hidden bg-slate-50 dark:bg-slate-900 touch-none select-none"
            style={{ aspectRatio: `${plan.width} / ${plan.height}`, backgroundImage: plan.background_image ? `url(${plan.background_image.startsWith('/api/') ? API + plan.background_image : plan.background_image})` : undefined, backgroundSize: '100% 100%' }}
            onPointerMove={onPointerMove} onPointerUp={onPointerUp} data-testid="floor-canvas">
            {shown.map((t, i) => {
              const style = {
                left: `${(t.pos_x / plan.width) * 100}%`, top: `${(t.pos_y / plan.height) * 100}%`,
                width: `${(t.width / plan.width) * 100}%`, height: `${(t.height / plan.height) * 100}%`,
              };
              const isProposal = !!proposal;
              return (
                <div key={t.id || `p${i}`} style={style}
                  onPointerDown={isProposal ? undefined : (e) => onPointerDown(e, t)}
                  className={`absolute flex flex-col items-center justify-center text-[11px] font-semibold border-2 cursor-move
                    ${t.shape === 'round' ? 'rounded-full' : 'rounded-md'}
                    ${isProposal ? 'border-violet-500 bg-violet-200/80 text-violet-900' : selected === t.id ? 'border-teal-600 bg-teal-100 text-teal-900' : 'border-slate-500 bg-white/90 text-slate-800'}`}>
                  <span>{t.table_number}</span><span className="font-normal">{t.capacity}p</span>
                </div>
              );
            })}
            {shown.length === 0 && <p className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">Upload your floor plan image and use AI layout, or add tables.</p>}
          </div>
          <div>{sel && !proposal ? <TableEditor api={api} table={sel} onChanged={load} onClose={() => setSelected(null)} /> :
            <p className="text-sm text-slate-500">Drag tables to move them. Click a table to edit seats, shape or section.</p>}</div>
        </div>
      ) : (
        <Card><CardContent className="py-10 text-center text-sm text-slate-500">Create an area (e.g. Main Dining) to start your floor plan.</CardContent></Card>
      )}

      {aiOpen && plan && <AILayoutDialog api={api} plan={plan} onClose={() => setAiOpen(false)} onProposed={(t) => { setAiOpen(false); setProposal(t); }} />}
    </div>
  );
}

function AILayoutDialog({ api, plan, onClose, onProposed }) {
  const [description, setDescription] = useState('');
  const [useImage, setUseImage] = useState(!!plan.background_image);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api(`${TM}/floor-plans/${plan.id}/ai-layout`, { method: 'POST', body: JSON.stringify({ description, use_image: useImage }) });
      onProposed(r.tables);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-5 space-y-3">
          <p className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-600" /> AI table layout</p>
          <p className="text-sm text-slate-500">AI reads your floor plan image and/or your description and proposes the tables. You review before anything is saved.</p>
          <div className="flex items-center justify-between">
            <span className="text-sm">Use the uploaded floor plan image</span>
            <Switch checked={useImage} onCheckedChange={setUseImage} disabled={!plan.background_image} />
          </div>
          <textarea rows={4} className="w-full rounded-md border border-slate-200 bg-white dark:bg-slate-800 p-2 text-sm"
            placeholder="e.g. 6 two-tops along the window wall, 4 round four-tops in the middle, 3 booths for 6 on the left, bar with 8 seats"
            value={description} onChange={(e) => setDescription(e.target.value)} data-testid="ai-description" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={run} disabled={busy || (!useImage && !description.trim())} data-testid="ai-run-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Propose layout'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AddTableButton({ api, planId, onAdded }) {
  const add = async () => {
    const number = window.prompt('Table number (e.g. 12, B2, P1)');
    if (!number) return;
    const seats = Number(window.prompt('Seats', '4')) || 4;
    try {
      await api(`${TM}/tables`, { method: 'POST', body: JSON.stringify({ table_number: number, capacity: seats, shape: 'square', pos_x: 20, pos_y: 20, floor_plan_id: planId }) });
      onAdded();
    } catch (e) { toast.error(e.message); }
  };
  return <Button variant="outline" size="sm" onClick={add}><Plus className="w-4 h-4 mr-1" /> Table</Button>;
}

function TableEditor({ api, table, onChanged, onClose }) {
  const [form, setForm] = useState({ table_number: table.table_number, capacity: table.capacity, shape: table.shape || 'square', section: table.section || '' });
  useEffect(() => { setForm({ table_number: table.table_number, capacity: table.capacity, shape: table.shape || 'square', section: table.section || '' }); }, [table.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    try {
      await api(`${TM}/tables/${table.id}`, { method: 'PATCH', body: JSON.stringify({ ...form, capacity: Number(form.capacity), section: form.section || null }) });
      toast.success('Table saved'); onChanged();
    } catch (e) { toast.error(e.message); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove table ${table.table_number}?`)) return;
    try { await api(`${TM}/tables/${table.id}`, { method: 'DELETE' }); onClose(); onChanged(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Card data-testid="table-editor"><CardContent className="p-4 space-y-2">
      <div className="flex justify-between items-center"><p className="font-semibold">Table {table.table_number}</p><button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button></div>
      <Input value={form.table_number} onChange={(e) => setForm({ ...form, table_number: e.target.value })} placeholder="Number" />
      <Input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="Seats" />
      <select className="w-full h-9 rounded-md border border-slate-200 bg-white dark:bg-slate-800 px-2 text-sm" value={form.shape} onChange={(e) => setForm({ ...form, shape: e.target.value })}>
        <option value="square">Square</option><option value="round">Round</option><option value="rectangle">Rectangle</option>
      </select>
      <Input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} placeholder="Section (Main, Patio, Bar)" />
      <div className="flex gap-2"><Button onClick={save} className="flex-1">Save</Button><Button variant="outline" onClick={remove}><Trash2 className="w-4 h-4" /></Button></div>
      <p className="text-xs text-slate-500">Now: {table.current_status}{table.current_reservation ? ` · ${table.current_reservation.customer_name} ${table.current_reservation.time}` : ''}</p>
    </CardContent></Card>
  );
}

/* ===================== Booking settings ===================== */

const SETTING_FIELDS = [
  ['slot_duration_minutes', 'Each sitting lasts (minutes)'],
  ['slot_interval_minutes', 'Offer a time every (minutes)'],
  ['buffer_minutes', 'Buffer between sittings (minutes)'],
  ['min_advance_hours', 'Book at least (hours ahead)'],
  ['max_advance_days', 'Book up to (days ahead)'],
  ['min_party_size', 'Smallest party'],
  ['max_party_size', 'Largest party online'],
  ['max_reservations_per_slot', 'Most bookings starting at one time'],
];

function SettingsTab({ api }) {
  const [s, setS] = useState(null);
  useEffect(() => { api(`${TM}/settings`).then(r => setS(r.settings || r)).catch(e => toast.error(e.message)); }, [api]);
  if (!s) return <p className="text-sm text-slate-500">Loading…</p>;
  const save = async () => {
    try {
      const body = { accept_reservations: s.accept_reservations, auto_confirm: s.auto_confirm };
      SETTING_FIELDS.forEach(([k]) => { body[k] = Number(s[k]); });
      await api(`${TM}/settings`, { method: 'PUT', body: JSON.stringify(body) });
      toast.success('Booking settings saved');
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Card><CardContent className="p-5 space-y-4" data-testid="booking-settings">
      <div className="flex items-center justify-between"><span className="text-sm font-medium">Take reservations online</span><Switch checked={!!s.accept_reservations} onCheckedChange={(v) => setS({ ...s, accept_reservations: v })} /></div>
      <div className="flex items-center justify-between"><span className="text-sm font-medium">Confirm automatically (off = you approve each booking)</span><Switch checked={!!s.auto_confirm} onCheckedChange={(v) => setS({ ...s, auto_confirm: v })} /></div>
      <div className="grid md:grid-cols-2 gap-3">
        {SETTING_FIELDS.map(([k, label]) => (
          <div key={k}><p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{label}</p>
            <Input type="number" min="0" value={s[k] ?? ''} onChange={(e) => setS({ ...s, [k]: e.target.value })} /></div>
        ))}
      </div>
      <p className="text-xs text-slate-500">Guests verify their phone by text the first time they book; after that their device books without a code.</p>
      <Button onClick={save}><Save className="w-4 h-4 mr-1" /> Save</Button>
    </CardContent></Card>
  );
}
